import type { ProductLine, SubscriptionInfo } from '@/graphql/graphql';

const PRODUCT_RANK: Record<ProductLine, number> = { VIEWER: 0, PRO: 1 };
export const productAtLeast = (current: ProductLine, required: ProductLine): boolean =>
  PRODUCT_RANK[current] >= PRODUCT_RANK[required];

const VOIDING_BILLING_STATUSES = ['CANCELED', 'UNPAID', 'NONE'];

export interface FeatureGate {
  minProduct?: ProductLine;
  minStorageBytes?: number;
}

export type FeatureKey =
  | 'viewer.clipBox'
  | 'viewer.exportClipBox'
  | 'viewer.elevationProfile'
  | 'viewer.stockpile'
  | 'viewer.pipe'
  | 'viewer.addLine'
  | 'viewer.addPolygon'
  | 'sharing.publicLink'
  | 'organization.authBranding'
  | 'processing.generateRcs'
  | 'analysis.rainfall'
  | 'analysis.deviation'
  | 'analysis.groundClassify'
  | 'analysis.meshExport'
  | 'export.vectorBackend'
  | 'report.pdf';

// TODO(owner): finalize each gate. Placeholder = available on any Viewer plan.
// Examples of the two axes:
//   { minStorageBytes: 500_000_000_000 }  → requires Team (500 GB) or larger
//   { minProduct: 'PRO' }                 → Professional only
export const FEATURE_GATES: Record<FeatureKey, FeatureGate> = {
  'viewer.clipBox': {},
  'viewer.exportClipBox': { minProduct: 'PRO' },
  'viewer.elevationProfile': {},
  'viewer.stockpile': {},
  'viewer.pipe': { minProduct: 'PRO' },
  'viewer.addLine': {},
  'viewer.addPolygon': {},
  'sharing.publicLink': {},
  'organization.authBranding': {},
  'processing.generateRcs': { minProduct: 'PRO' },
  'analysis.rainfall': { minProduct: 'PRO' },
  'analysis.deviation': { minProduct: 'PRO' },
  'analysis.groundClassify': { minProduct: 'PRO' },
  'analysis.meshExport': { minProduct: 'PRO' },
  'export.vectorBackend': {},
  'report.pdf': { minProduct: 'PRO' },
};

const WHITELISTED_EMAIL_DOMAINS = ['actegon.com', 'skygis.cloud'];
export const isWhitelistedEmail = (email: string | null | undefined): boolean => {
  if (!email) return false;
  const at = email.lastIndexOf('@');
  if (at < 0) return false;
  return WHITELISTED_EMAIL_DOMAINS.includes(email.slice(at + 1).toLowerCase());
};

// Per-feature organization overrides. Organizations listed here bypass the
// normal subscription gate for the specified feature.
const WHITELISTED_ORGANIZATIONS: Partial<Record<FeatureKey, string[]>> = {
  'processing.generateRcs': ['faab642c-0474-4fba-91b4-a261a03f2dc2'], // topocrew
};

export const isFeatureAllowed = (
  sub: SubscriptionInfo,
  email: string | null | undefined,
  key: FeatureKey,
  organizationId?: string | null,
): boolean => {
  if (isWhitelistedEmail(email)) return true;
  if (organizationId && WHITELISTED_ORGANIZATIONS[key]?.includes(organizationId)) return true;
  if (VOIDING_BILLING_STATUSES.includes(sub.billingStatus)) return false;
  const gate = FEATURE_GATES[key];
  if (gate.minProduct && !productAtLeast(sub.productLine, gate.minProduct)) return false;
  if (gate.minStorageBytes != null && sub.storageLimitBytes < gate.minStorageBytes) return false;
  return true;
};
